import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, descargarExcel, formatApiErrorDetail } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { ConciliarStripe } from "@/components/ConciliarStripe";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Download, FileDown, Undo2, Loader2, Euro, Repeat, Ticket, RotateCcw, Clock } from "lucide-react";

const ESTADOS = {
  pagado: { label: "Cobrado", cls: "bg-emerald-600 hover:bg-emerald-600 text-white" },
  pendiente: { label: "Pendiente de cobro", cls: "bg-amber-500 hover:bg-amber-500 text-white" },
  devuelto: { label: "Devuelto", cls: "bg-destructive hover:bg-destructive text-white" },
  anulado: { label: "Anulado", cls: "bg-muted text-muted-foreground" },
};

const Metric = ({ icon: Icon, label, valor, detalle, testid }) => (
  <Card className="rounded-2xl p-6 border-border shadow-sm" data-testid={testid}>
    <div className="flex items-start justify-between">
      <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">{label}</p>
      <Icon className="h-4 w-4 text-primary" />
    </div>
    <p className="font-display text-3xl font-extrabold mt-4">{valor}</p>
    {detalle && <p className="text-xs text-muted-foreground mt-2">{detalle}</p>}
  </Card>
);

export default function PagosFacturacion() {
  const [params] = useSearchParams();
  const { user } = useAuth();
  const [cobros, setCobros] = useState([]);
  const [resumen, setResumen] = useState(null);
  const [sedes, setSedes] = useState([]);
  const [filtros, setFiltros] = useState({
    estado: params.get("estado") || "todos",
    grupo: params.get("grupo") || "todos",
    sede_id: params.get("sede_id") || "todas",
    desde: "", hasta: "",
  });
  const [cargando, setCargando] = useState(true);
  const [devolucion, setDevolucion] = useState(null);
  const [enviando, setEnviando] = useState(false);

  const cargar = useCallback(async () => {
    setCargando(true);
    const params = {};
    if (filtros.estado !== "todos") params.estado = filtros.estado;
    if (filtros.sede_id !== "todas") params.sede_id = filtros.sede_id;
    if (filtros.desde) params.desde = filtros.desde;
    if (filtros.hasta) params.hasta = filtros.hasta;
    try {
      const [{ data: lista }, { data: res }] = await Promise.all([
        api.get("/cobros", { params }), api.get("/cobros/resumen", { params }),
      ]);
      setCobros(filtros.grupo === "todos" ? lista : lista.filter((c) => c.grupo === filtros.grupo));
      setResumen(res);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setCargando(false);
    }
  }, [filtros]);

  useEffect(() => { cargar(); }, [cargar]);
  useEffect(() => { api.get("/sedes").then(({ data }) => setSedes(data)); }, []);

  const descargarPdf = async (id, numero) => {
    try {
      const { data } = await api.get(`/facturas/${id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${numero.replace("/", "-")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("No se pudo descargar el PDF");
    }
  };

  const confirmarDevolucion = async () => {
    setEnviando(true);
    try {
      const { data } = await api.post(`/cobros/${devolucion.session_id}/devolucion`, {
        importe: Number(devolucion.importe) || undefined, motivo: devolucion.motivo,
        solo_registrar: !!devolucion.solo_registrar,
      });
      toast.success(`Devolución registrada: ${data.importe} €`
        + (data.stripe_refund_id ? " · reembolsado en Stripe" : "")
        + (data.rectificativa ? ` · rectificativa ${data.rectificativa.numero}` : ""));
      setDevolucion(null);
      cargar();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail), { duration: 9000 });
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div data-testid="pagos-facturacion-page">
      <PageHeader title="Pagos y facturación"
        subtitle="Todos los cobros recibidos, sus facturas y las devoluciones" />

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-6 mb-8">
        <Metric icon={Euro} label="Cobrado (neto)" testid="metric-cobrado"
          valor={resumen ? `${resumen.cobrado_total} €` : "—"}
          detalle={resumen ? `${resumen.cobrado_mes} € este mes` : ""} />
        <Metric icon={Repeat} label="Suscripciones" testid="metric-suscripciones"
          valor={resumen ? `${resumen.suscripciones.importe} €` : "—"}
          detalle={resumen ? `${resumen.suscripciones.num} cobro(s)` : ""} />
        <Metric icon={Ticket} label="Pagos únicos" testid="metric-unicos"
          valor={resumen ? `${resumen.unicos.importe} €` : "—"}
          detalle={resumen ? `${resumen.unicos.num} cobro(s)` : ""} />
        <Metric icon={Clock} label="Pendiente de cobro" testid="metric-pendientes"
          valor={resumen ? `${resumen.pendientes.importe} €` : "—"}
          detalle={resumen ? `${resumen.pendientes.num} pago(s)` : ""} />
        <Metric icon={RotateCcw} label="Devoluciones" testid="metric-devoluciones"
          valor={resumen ? `${resumen.devoluciones.importe} €` : "—"}
          detalle={resumen ? `${resumen.devoluciones.num} devolución(es)` : ""} />
      </div>

      <div className="flex flex-wrap gap-3 mb-6 items-end">
        <div className="grid gap-1.5">
          <Label className="text-xs">Estado</Label>
          <Select value={filtros.estado} onValueChange={(v) => setFiltros((f) => ({ ...f, estado: v }))}>
            <SelectTrigger className="w-52" data-testid="filtro-estado"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Todos los estados</SelectItem>
              <SelectItem value="pagado">Cobrados</SelectItem>
              <SelectItem value="pendiente">Pendientes de cobro</SelectItem>
              <SelectItem value="devuelto">Devueltos</SelectItem>
              <SelectItem value="anulado">Anulados</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="grid gap-1.5">
          <Label className="text-xs">Concepto</Label>
          <Select value={filtros.grupo} onValueChange={(v) => setFiltros((f) => ({ ...f, grupo: v }))}>
            <SelectTrigger className="w-52" data-testid="filtro-grupo"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Suscripciones y únicos</SelectItem>
              <SelectItem value="suscripciones">Solo suscripciones</SelectItem>
              <SelectItem value="unicos">Solo pagos únicos y bonos</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="grid gap-1.5">
          <Label className="text-xs">Sede</Label>
          <Select value={filtros.sede_id} onValueChange={(v) => setFiltros((f) => ({ ...f, sede_id: v }))}>
            <SelectTrigger className="w-48" data-testid="filtro-sede"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="todas">Todas las sedes</SelectItem>
              {sedes.map((s) => <SelectItem key={s.id} value={s.id}>{s.nombre}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="grid gap-1.5">
          <Label className="text-xs">Desde</Label>
          <Input type="date" className="w-40" value={filtros.desde} data-testid="filtro-desde"
            onChange={(e) => setFiltros((f) => ({ ...f, desde: e.target.value }))} />
        </div>
        <div className="grid gap-1.5">
          <Label className="text-xs">Hasta</Label>
          <Input type="date" className="w-40" value={filtros.hasta} data-testid="filtro-hasta"
            onChange={(e) => setFiltros((f) => ({ ...f, hasta: e.target.value }))} />
        </div>
        <Button variant="outline" className="rounded-full font-semibold" data-testid="export-pagos-btn"
          onClick={() => descargarExcel("/reportes/pagos", "pagos.xlsx")}>
          <Download className="h-4 w-4 mr-2" /> Excel
        </Button>
        {user?.role === "admin" && (
          <ConciliarStripe sedeId={filtros.sede_id === "todas" ? "" : filtros.sede_id}
            onHecho={cargar} />
        )}
      </div>

      {cargando ? (
        <Loader2 className="h-5 w-5 animate-spin" />
      ) : cobros.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="cobros-empty">
          No hay pagos con estos filtros.
        </p>
      ) : (
        <div className="space-y-3">
          {cobros.map((c) => {
            const est = ESTADOS[c.estado] || { label: c.estado, cls: "" };
            return (
              <Card key={c.session_id} className="rounded-2xl p-5 border-border shadow-sm"
                data-testid={`cobro-${c.session_id}`}>
                <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="font-display font-bold">{c.concepto}</p>
                      <Badge className={`rounded-full ${est.cls}`}
                        data-testid={`cobro-estado-${c.session_id}`}>{est.label}</Badge>
                      <Badge variant="outline" className="rounded-full">
                        {c.via === "stripe" ? "Stripe" : "En la academia"}
                      </Badge>
                      {c.recurrente && <Badge variant="secondary" className="rounded-full">Recurrente</Badge>}
                    </div>
                    <p className="text-sm text-muted-foreground mt-1.5">
                      {c.familia} · {c.alumnos}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {c.fecha} · {c.sede} · {c.tipo_label}
                      {c.factura_numero ? ` · factura ${c.factura_numero}` : " · sin factura"}
                    </p>
                    {c.estado === "devuelto" && (
                      <p className="text-xs text-destructive font-semibold mt-1.5">
                        Devuelto {c.devuelto_importe} € el {c.devuelto_fecha}
                        {c.devuelto_motivo ? ` · ${c.devuelto_motivo}` : ""}
                        {c.rectificativa_numero ? ` · rectificativa ${c.rectificativa_numero}` : ""}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className="font-display text-xl font-extrabold">{c.importe} €</span>
                    {c.factura_id && (
                      <Button variant="outline" size="sm" className="rounded-full"
                        onClick={() => descargarPdf(c.factura_id, c.factura_numero)}
                        data-testid={`factura-pdf-${c.session_id}`}>
                        <FileDown className="h-4 w-4 mr-1.5" /> Factura
                      </Button>
                    )}
                    {c.rectificativa_id && (
                      <Button variant="outline" size="sm" className="rounded-full text-destructive"
                        onClick={() => descargarPdf(c.rectificativa_id, c.rectificativa_numero)}
                        data-testid={`rectificativa-pdf-${c.session_id}`}>
                        <FileDown className="h-4 w-4 mr-1.5" /> Rectificativa
                      </Button>
                    )}
                    {c.puede_devolver && (
                      <Button variant="outline" size="sm" className="rounded-full text-destructive"
                        data-testid={`devolver-${c.session_id}`}
                        onClick={() => setDevolucion({ ...c, importe: c.importe, motivo: "" })}>
                        <Undo2 className="h-4 w-4 mr-1.5" /> Devolver
                      </Button>
                    )}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={!!devolucion} onOpenChange={(v) => !v && setDevolucion(null)}>
        <DialogContent className="rounded-2xl" data-testid="devolucion-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Devolver un cobro</DialogTitle>
          </DialogHeader>
          {devolucion && (
            <div className="grid gap-4">
              <p className="text-sm text-muted-foreground">
                {devolucion.concepto} · {devolucion.familia} · cobrado {devolucion.importe} €
                {devolucion.via === "stripe"
                  ? ". El reembolso se ejecuta en Stripe y la familia lo recibe en su tarjeta en 5-10 días."
                  : ". Se registrará la devolución; el dinero lo devuelves tú en la academia."}
              </p>
              <div className="grid gap-2">
                <Label>Importe a devolver (€)</Label>
                <Input type="number" step="0.01" value={devolucion.importe}
                  data-testid="devolucion-importe"
                  onChange={(e) => setDevolucion((d) => ({ ...d, importe: e.target.value }))} />
              </div>
              <div className="grid gap-2">
                <Label>Motivo</Label>
                <Input value={devolucion.motivo} placeholder="Baja del alumno, cobro duplicado…"
                  data-testid="devolucion-motivo"
                  onChange={(e) => setDevolucion((d) => ({ ...d, motivo: e.target.value }))} />
              </div>
              {devolucion.via === "stripe" && (
                <label className="flex items-start gap-3 text-sm cursor-pointer">
                  <input type="checkbox" className="mt-1" checked={!!devolucion.solo_registrar}
                    data-testid="devolucion-solo-registrar"
                    onChange={(e) => setDevolucion((d) => ({ ...d, solo_registrar: e.target.checked }))} />
                  <span>
                    Ya lo he devuelto desde el panel de Stripe: solo registrarlo aquí y emitir la
                    factura rectificativa (no se intentará reembolsar otra vez).
                  </span>
                </label>
              )}
            </div>
          )}
          <DialogFooter>
            <Button className="rounded-full font-semibold" onClick={confirmarDevolucion}
              disabled={enviando} data-testid="devolucion-confirmar">
              {enviando ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirmar devolución"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
